/**
 * @file Login.tsx
 * @author Arunkumar Annamalai
 * @version 1.0.0
 * @description Login page component for the Shay Suite Admin application.
 *              This file provides the user authentication interface with form
 *              validation, API integration, and role-based navigation. It handles
 *              both root admin and company admin login with appropriate redirects.
 * @created Aug-2025
 * @lastModified Aug-2025
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { adminAPI } from '../services/api';

/**
 * Login Page Component
 * 
 * This component:
 * - Provides user authentication interface
 * - Handles form submission and API calls
 * - Manages loading states and error handling
 * - Implements password visibility toggle
 * - Redirects users based on their role and authentication status
 * - Integrates with toast notifications for user feedback
 * 
 * @returns {JSX.Element} Login page with authentication form
 */
const Login: React.FC = () => {
  // Form state management
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Context hooks for authentication and notifications
  const { login, isAuthenticated, user } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if there's a message from navigation
  const pendingMessage = location.state?.message;

  /**
   * Effect to handle navigation after successful authentication
   * Redirects users to appropriate pages based on their role and onboarding status
   */
  useEffect(() => {
    if (isAuthenticated) {
      console.log('=== Navigation Debug ===');
      console.log('isAuthenticated:', isAuthenticated);
      console.log('user object:', user);
      console.log('user?.type:', user?.type);
      console.log('user?.isRootAdmin:', user?.isRootAdmin);
      
      // Check if there's pending onboarding data
      const onboardingData = localStorage.getItem('onboardingData');
      console.log('onboardingData:', onboardingData);
      
      // Root admins should never go to onboarding - clear any pending onboarding data
      if (user?.isRootAdmin) {
        if (onboardingData) {
          console.log('Clearing onboarding data for root admin');
          localStorage.removeItem('onboardingData');
        }
        console.log('Navigating to /customers (root admin)');
        navigate('/customers');
      } else if (onboardingData) {
        console.log('Navigating to /onboarding (pending onboarding data)');
        navigate('/onboarding');
      } else if (user?.type === 'customer') {
        console.log('Navigating to /onboarding (customer)');
        navigate('/onboarding'); // Customers only have onboarding page
      } else {
        console.log('Navigating to /onboarding (company admin)');
        navigate('/onboarding'); // Company admins (non-root) go to onboarding
      }
    }
  }, [isAuthenticated, navigate, user]);

  /**
   * Handle form submission for user authentication
   * 
   * This function:
   * - Prevents default form submission behavior
   * - Sets loading state and clears previous errors
   * - Makes API call to authenticate user credentials
   * - Handles successful login with token storage
   * - Manages error states and user feedback
   * 
   * @param {React.FormEvent} e - Form submission event
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Make real API call to authenticate
      const response = await adminAPI.login({ email: username, password });
      console.log('Login response:', response.data);
      // The response interceptor should have already extracted the data
      const { token, refreshToken, user: userData } = response.data;
      console.log('Extracted token and user data:', { token: token ? 'present' : 'missing', refreshToken: refreshToken ? 'present' : 'missing', userData });
      login(token, refreshToken, userData);
      showSuccess('Login successful!');
    } catch (err: any) {
      const errorMessage = err.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          borderRadius: 2,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold' }}>
          Login
        </Typography>
        
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Enter your credentials to access the dashboard
        </Typography>

        {pendingMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {pendingMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            type="email"
          />
          
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={loading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'LOGIN'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login; 